import React, { useState, useEffect } from 'react';
import { Icons } from './AccountIcons';
import QuickStats from './QuickStats';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, LineElement, PointElement, Filler } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, LineElement, PointElement, Filler);

const NoDataMessage = () => (
  <div className="no-data-message">
    <p>Aucune activité enregistrée pour le moment.</p>
    <p>Commencez un quiz ou étudiez des flashcards pour voir vos statistiques !</p>
  </div>
);

const AccountDashboard = ({
  statsToday,
  statsMonth,
  statsTotal,
  streaks,
  weeklyProgress,
  categoryPerformance,
  badgeData,
  todayDisplay,
  monthDisplay,
  chartColors,
  activitySummary,
  getBoxProgressionByCategory,
  learnData,
  learnScore,
  userId,
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedCategory, setSelectedCategory] = useState('Art');
  const [expandedThemes, setExpandedThemes] = useState({});
  const [celebrationStats, setCelebrationStats] = useState({
    quizCount: 0,
    flashcardsStudied: 0,
    studyDays: 0,
  });

  const calculateCelebrationStats = (dailyActivity) => {
    console.log('AccountDashboard - dailyActivity reçu:', dailyActivity);

    let totalQuizCount = 0;
    let totalFlashcardsStudied = 0;
    const studyDays = dailyActivity ? Object.keys(dailyActivity).length : 0;

    if (dailyActivity) {
      Object.values(dailyActivity).forEach((dailyData) => {
        //console.log('AccountDashboard - Données quotidiennes:', dailyData);
        totalQuizCount += dailyData.quizCount || dailyData.quizzes || 0;
        totalFlashcardsStudied += dailyData.flashcardsStudied || dailyData.flashcards || 0;
      });
    }

    const calculatedStats = {
      quizCount: totalQuizCount,
      flashcardsStudied: totalFlashcardsStudied,
      studyDays,
    };

    console.log('AccountDashboard - Stats calculées pour celebration:', calculatedStats);
    return calculatedStats;
  };

  useEffect(() => {
    if (activitySummary && activitySummary.dailyActivity) {
      const calculatedStats = calculateCelebrationStats(activitySummary.dailyActivity);
      setCelebrationStats(calculatedStats);
    } else {
      console.log('AccountDashboard - activitySummary ou dailyActivity est undefined:', activitySummary);
    }
  }, [activitySummary]);

  const calculateProgress = (current, threshold) => Math.min(100, Math.round((current / threshold) * 100));
  const categories = [
    "Divers",
    'Géographie',
    'Histoire',
    'Art',
    'Littérature et Langues',
    'Culture Pop',
    'Science et Nature',
    'Sport'
  ];
  const categories_flashcards = [
    'Géographie',
    'Histoire',
    'Art',
    'Littérature et Langues',
    'Culture Pop',
    'Science et Nature',
    'Sport'
  ];
  const boxProgressionData = getBoxProgressionByCategory(selectedCategory);

  const subThemes = {
    'Géographie': ['Capitale', 'Drapeau', 'Monnaies'],
    'Science et Nature': ['Nature', 'Astronomie', 'Prix Nobel', 'Médaille Fields', 'Prix Turing'],
    'Littérature et Langues': ['Littérature','Prix Goncourt'],
    'Sport': ['Football','Formule 1'],
    'Culture Pop': ['Cinéma', 'Musique', 'Fort Boyard'],
    'Art': ['Architecture', 'Sculpture', 'Peinture']

  };

  const toggleSubThemes = (theme) => {
    setExpandedThemes((prev) => ({
      ...prev,
      [theme]: !prev[theme],
    }));
  };

  const getFlashcardScore = (box) => {
    switch (parseInt(box)) {
      case 1: return 0;
      case 2: return 40;
      case 3: return 70;
      case 4: return 90;
      case 5: return 100;
      default: return 0;
    }
  };

  const calculateSubThemeScore = (theme, subTheme) => {
    if (!learnData || Object.keys(learnData).length === 0) return 0;
    const flashcardIds = Object.keys(learnData);
    const relevantFlashcards = flashcardIds.filter((flashcardId) => {
      const themes = learnData[flashcardId].themes || [];
      return themes.includes(subTheme);
    });
    if (relevantFlashcards.length === 0) return 0;
    const totalScore = relevantFlashcards.reduce((sum, flashcardId) => {
      const box = learnData[flashcardId].box || '1';
      return sum + getFlashcardScore(box);
    }, 0);
    return totalScore / relevantFlashcards.length;
  };

  const getThemeScoresByCategory = (category) => {
    if (!statsTotal.sessionHistory) {
      console.log('sessionHistory est vide ou non défini');
      return { labels: [], data: [] };
    }
  
    // Filtrer et trier les sessions pour la catégorie
    const sessions = statsTotal.sessionHistory
      .filter((session) => session.category === category)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  
    // Générer les labels et data à partir des sessions existantes
    let labels = sessions.map((session) => {
      const date = new Date(session.date);
      return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    });
    let data = sessions.map((session) => session.score || 0);
  
    // Obtenir la date actuelle
    const today = new Date();
    const todayStr = today.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  
    // Vérifier si la date actuelle est déjà dans les labels
    const todayExists = labels.includes(todayStr);
  
    // Si la date actuelle n'existe pas, ajouter un point avec le score actuel
    if (!todayExists) {
      labels.push(todayStr);
      // Utiliser le score actuel de learnScore pour la catégorie, ou 0 si non défini
      const currentScore = learnScore && learnScore[category] ? learnScore[category] : 0;
      data.push(currentScore);
    }
  
    return { labels, data };
  };

  const getQuizAttemptsByCategory = () => {
    // Si aucune donnée n'existe, retourner un tableau de zéros
    if (!statsTotal.performance?.byTheme) return categories.map(() => 0);
  
    // Initialiser un objet pour cumuler les tentatives par catégorie
    const attemptsByCategory = {};
    categories.forEach(cat => attemptsByCategory[cat] = 0);
  
    // Parcourir tous les thèmes dans statsTotal.performance.byTheme
    Object.entries(statsTotal.performance.byTheme).forEach(([theme, stats]) => {
      // Chercher si le thème est une catégorie principale
      const isMainCategory = categories.includes(theme);
      if (isMainCategory) {
        attemptsByCategory[theme] += stats.attempts || 0;
      } else {
        // Si ce n'est pas une catégorie principale, vérifier si c'est un sous-thème
        let found = false;
        for (const [mainCat, subThemeList] of Object.entries(subThemes)) {
          if (subThemeList.includes(theme)) {
            attemptsByCategory[mainCat] += stats.attempts || 0;
            found = true;
            break;
          }
        }
        // Si le thème n'est ni une catégorie principale ni un sous-thème connu, le mettre dans "Divers"
        if (!found) {
          attemptsByCategory["Divers"] += stats.attempts || 0;
        }
      }
    });
  
    // Retourner les données dans l'ordre des categories
    return categories.map(cat => attemptsByCategory[cat]);
  };

  const themeScoresData = getThemeScoresByCategory(selectedCategory);
  const quizAttemptsData = getQuizAttemptsByCategory();

  const quizCount = celebrationStats.quizCount;
  const flashcardsStudied = celebrationStats.flashcardsStudied;
  const studyDays = celebrationStats.studyDays;

  const getLevel = (value, thresholds) => {
    if (value <= thresholds.level1) return 1;
    if (value <= thresholds.level2) return 2;
    if (value <= thresholds.level3) return 3;
    if (value <= thresholds.level4) return 4;
    if (value <= thresholds.level5) return 5;
    if (value <= thresholds.level6) return 6;
    if (value <= thresholds.level7) return 7;
    if (value <= thresholds.level8) return 8;
    if (value <= thresholds.level9) return 9;
    return 10;
  };

  const quizLevel = getLevel(quizCount, {
    level1: 5,
    level2: 10,
    level3: 20,
    level4: 30,
    level5: 40,
    level6: 50,
    level7: 75,
    level8: 100,
    level9: 150,
  });

  const flashcardLevel = getLevel(flashcardsStudied, {
    level1: 50,
    level2: 100,
    level3: 200,
    level4: 300,
    level5: 400,
    level6: 500,
    level7: 750,
    level8: 1000,
    level9: 1500,
  });

  const studyDaysLevel = getLevel(studyDays, {
    level1: 7,
    level2: 14,
    level3: 30,
    level4: 45,
    level5: 60,
    level6: 90,
    level7: 120,
    level8: 180,
    level9: 270,
  });

  const averageLevel = Math.round((quizLevel + flashcardLevel + studyDaysLevel) / 3);

  let celebrationTitle = '';
  let celebrationMessage = '';

  switch (averageLevel) {
    case 1:
      celebrationTitle = 'Premier Pas !';
      celebrationMessage = 'Vous venez de commencer votre aventure. Chaque quiz et flashcard vous rapproche de vos objectifs !';
      break;
    case 2:
      celebrationTitle = 'Bon Début !';
      celebrationMessage = 'Vous êtes sur la bonne voie ! Continuez à explorer et à apprendre pour progresser encore plus.';
      break;
    case 3:
      celebrationTitle = 'Progrès Prometteurs !';
      celebrationMessage = 'Vous commencez à prendre vos marques. Continuez à étudier régulièrement pour booster vos compétences !';
      break;
    case 4:
      celebrationTitle = 'Beau Travail !';
      celebrationMessage = 'Vous faites de grands progrès dans votre apprentissage. Restez motivé pour atteindre de nouveaux sommets !';
      break;
    case 5:
      celebrationTitle = 'Niveau Intermédiaire !';
      celebrationMessage = 'Vous avez atteint un niveau impressionnant. Continuez à approfondir vos connaissances pour devenir un expert !';
      break;
    case 6:
      celebrationTitle = 'Super Performance !';
      celebrationMessage = 'Votre engagement est remarquable. Vous êtes sur le point de devenir un véritable maître dans vos études !';
      break;
    case 7:
      celebrationTitle = 'Expert en Herbe !';
      celebrationMessage = 'Vous êtes presque un expert ! Continuez à maintenir ce rythme pour atteindre l’excellence.';
      break;
    case 8:
      celebrationTitle = 'Maîtrise Impressionnante !';
      celebrationMessage = 'Votre maîtrise est incroyable. Vous êtes un modèle d’apprentissage, continuez à exceller !';
      break;
    case 9:
      celebrationTitle = 'Champion de l’Apprentissage !';
      celebrationMessage = 'Vous êtes un champion ! Votre dévouement et votre persévérance sont une source d’inspiration.';
      break;
    case 10:
    default:
      celebrationTitle = 'Légende Vivante !';
      celebrationMessage = 'Vous êtes une légende de l’apprentissage ! Votre passion et votre engagement sont inégalés. Continuez à briller !';
      break;
  }

  return (
    <div className="account-dashboard">
      <h1 className="account-title">Tableau de Bord</h1>
      <div className="activity-summary">
        <QuickStats
          userId={userId}
          todayDisplay={todayDisplay}
          monthDisplay={monthDisplay}
        />
        <div className="metric-selector">
          <button
            className={`metric-btn ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <Icons.Activity /> Vue d'ensemble
          </button>
          <button
            className={`metric-btn ${activeTab === 'progression' ? 'active' : ''}`}
            onClick={() => setActiveTab('progression')}
          >
            <Icons.TrendingUp /> Progression
          </button>
          <button
            className={`metric-btn ${activeTab === 'achievements' ? 'active' : ''}`}
            onClick={() => setActiveTab('achievements')}
          >
            <Icons.Award /> Réalisations
          </button>
        </div>

        {activeTab === 'overview' && (
          <div className="dashboard-grid overview visible">
            {statsTotal.quizCount + statsTotal.flashcardsStudied === 0 ? (
              <NoDataMessage />
            ) : (
              <>
                <div className="dashboard-card">
                  <h3>Activité Hebdomadaire</h3>
                  <div className="chart-container">
                    <Bar
                      data={{
                        labels: weeklyProgress.labels,
                        datasets: [
                          {
                            label: 'Quiz',
                            data: weeklyProgress.quizData,
                            backgroundColor: 'rgba(54, 162, 235, 0.7)',
                            borderColor: 'rgba(54, 162, 235, 1)',
                            borderWidth: 0,
                            borderRadius: 6,
                            barThickness: 14,
                          },
                          {
                            label: 'Flashcards',
                            data: weeklyProgress.flashcardData,
                            backgroundColor: 'rgba(255, 99, 132, 0.7)',
                            borderColor: 'rgba(255, 99, 132, 1)',
                            borderWidth: 0,
                            borderRadius: 6,
                            barThickness: 14,
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: { beginAtZero: true, grid: { display: true, color: 'rgba(0, 0, 0, 0.05)' }, ticks: { font: { size: 12 } }, border: { dash: [4, 4] } },
                          x: { grid: { display: false }, ticks: { font: { size: 12, weight: 'bold' } } },
                        },
                        plugins: {
                          legend: { display: true, position: 'top', labels: { boxWidth: 12, usePointStyle: true, pointStyle: 'circle', padding: 20, font: { size: 12 } } },
                        },
                      }}
                    />
                  </div>
                </div>
                <div className="dashboard-card">
                  <h3>Répartition des Quiz (Total)</h3>
                  <div className="chart-container" style={{ height: '300px' }}>
                    <Doughnut
                      data={{
                        labels: categories,
                        datasets: [
                          {
                            data: quizAttemptsData,
                            backgroundColor: chartColors,
                            borderWidth: 0,
                            hoverOffset: 5,
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        cutout: '70%',
                        plugins: {
                          legend: { position: 'bottom', labels: { font: { size: 10 }, padding: 10, usePointStyle: true, pointStyle: 'circle', boxWidth: 8 } },
                          tooltip: {
                            callbacks: {
                              label: (context) => `${context.label}: ${context.raw} (${Math.round((context.raw / context.dataset.data.reduce((a, b) => a + b, 0)) * 100)}%)`,
                            },
                            titleFont: { size: 13 },
                            bodyFont: { size: 12 },
                            padding: 10,
                            boxPadding: 3,
                          },
                        },
                      }}
                    />
                  </div>
                </div>
                <div className="dashboard-card">
                  <h3>Répartition des Flashcards (Total)</h3>
                  <div className="chart-container" style={{ height: '300px' }}>
                    <Doughnut
                      data={{
                        labels: categories_flashcards,
                        datasets: [
                          {
                            data: categories_flashcards.map((cat) => statsTotal.flashcardByCategory[cat] || 0),
                            backgroundColor: chartColors,
                            borderWidth: 0,
                            hoverOffset: 5,
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        cutout: '70%',
                        plugins: {
                          legend: { position: 'bottom', labels: { font: { size: 10 }, padding: 10, usePointStyle: true, pointStyle: 'circle', boxWidth: 8 } },
                          tooltip: {
                            callbacks: {
                              label: (context) => `${context.label}: ${context.raw} (${Math.round((context.raw / context.dataset.data.reduce((a, b) => a + b, 0)) * 100)}%)`,
                            },
                            titleFont: { size: 13 },
                            bodyFont: { size: 12 },
                            padding: 10,
                            boxPadding: 3,
                          },
                        },
                      }}
                    />
                  </div>
                </div>
                <div className="dashboard-card">
                  <h3>Performance par Catégorie</h3>
                  <div className="category-performance-container">
                    {categoryPerformance && categoryPerformance.labels && categoryPerformance.labels.length > 0 ? (
                      categoryPerformance.labels.map((label, index) => (
                        <div key={label} className="theme-performance-group">
                          <div
                            className="performance-bar-wrapper"
                            onClick={() => toggleSubThemes(label)}
                            style={{ cursor: 'pointer' }}
                          >
                            <div className="performance-bar-label">{label}</div>
                            <div className="performance-bar">
                              <div
                                className="performance-bar-fill"
                                style={{
                                  width: `${categoryPerformance.data[index]}%`,
                                  backgroundColor: chartColors[index]?.replace('0.8', '0.9') || 'rgba(0, 0, 0, 0.9)',
                                }}
                              ></div>
                            </div>
                            <div className="performance-bar-value">{categoryPerformance.data[index].toFixed(2)}%</div>
                          </div>
                          {expandedThemes[label] && subThemes[label] && (
                            <div className="subtheme-performance-group">
                              {subThemes[label].map((subTheme) => {
                                const subThemeScore = calculateSubThemeScore(label, subTheme);
                                return (
                                  <div key={subTheme} className="performance-bar-wrapper sub-theme">
                                    <div className="performance-bar-label">{subTheme}</div>
                                    <div className="performance-bar">
                                      <div
                                        className="performance-bar-fill"
                                        style={{
                                          width: `${subThemeScore}%`,
                                          backgroundColor: chartColors[index]?.replace('0.8', '0.5') || 'rgba(0, 0, 0, 0.5)',
                                        }}
                                      ></div>
                                    </div>
                                    <div className="performance-bar-value">{subThemeScore.toFixed(2)}%</div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <p>Aucune donnée de performance disponible.</p>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'progression' && (
          <div className="dashboard-grid progression visible">
            {statsTotal.quizCount + statsTotal.flashcardsStudied === 0 ? (
              <NoDataMessage />
            ) : (
              <div className="dashboard-card wide-card">
                <h3>Répartition dans les Boîtes</h3>
                <p>Vos flashcards progressent à travers 5 boîtes selon votre performance.</p>
                <div className="category-tabs">
                  {categories.map((cat) => (
                    <button
                      key={cat}
                      className={`category-tab ${selectedCategory === cat ? 'active' : ''}`}
                      onClick={() => setSelectedCategory(cat)}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
                <div className="chart-row">
                  <div>
                    <div className="chart-container" style={{ height: '300px' }}>
                      <Bar
                        data={{
                          labels: boxProgressionData.labels,
                          datasets: [
                            {
                              label: selectedCategory,
                              data: boxProgressionData.data,
                              backgroundColor: chartColors.slice(0, 5),
                              borderWidth: 0,
                              borderRadius: 8,
                              barPercentage: 0.6,
                              categoryPercentage: 0.7,
                            },
                          ],
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            y: { beginAtZero: true, grid: { display: true, color: 'rgba(0, 0, 0, 0.05)' }, ticks: { font: { size: 12 } }, border: { dash: [4, 4] } },
                            x: { grid: { display: false }, ticks: { font: { size: 12, weight: 'bold' } } },
                          },
                          plugins: {
                            legend: { display: false },
                            tooltip: {
                              callbacks: {
                                title: (items) => `${items[0].label} - ${selectedCategory}`,
                                label: (context) => `${context.raw} flashcards`,
                              },
                              titleFont: { size: 14, weight: 'bold' },
                              bodyFont: { size: 13 },
                              padding: 12,
                              boxPadding: 4,
                            },
                          },
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <h4>Score Total du Thème ({selectedCategory})</h4>
                    <div className="chart-container" style={{ height: '300px' }}>
                      <Line
                        data={{
                          labels: themeScoresData.labels,
                          datasets: [
                            {
                              label: `Score Total (${selectedCategory})`,
                              data: themeScoresData.data,
                              borderColor: chartColors[1],
                              backgroundColor: chartColors[1].replace('0.7', '0.2'),
                              fill: true,
                              tension: 0.4,
                              pointRadius: 5,
                              pointHoverRadius: 7,
                            },
                          ],
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            y: { beginAtZero: true, max: 100, grid: { display: true, color: 'rgba(0, 0, 0, 0.05)' }, ticks: { font: { size: 12 } } },
                            x: {
                              grid: { display: false },
                              ticks: {
                                font: { size: 12 },
                                maxRotation: 45,
                                minRotation: 45,
                                maxTicksLimit: 5,
                                autoSkip: true,
                              },
                            },
                          },
                          plugins: {
                            legend: { display: true, position: 'top' },
                            tooltip: { callbacks: { label: (context) => `Score Total: ${context.raw.toFixed(2)}%` } },
                          },
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'achievements' && (
          <>
            <div className="dashboard-grid achievements visible">
              <div className="dashboard-card wide-card">
                <h3>Badges Obtenus</h3>
                <div className="badges-container">
                  {badgeData.earned.length > 0 ? (
                    badgeData.earned.map((badge, index) => (
                      <div key={index} className="badge-item earned">
                        <div className="badge-icon">{badge.icon}</div>
                        <div className="badge-details">
                          <h4>{badge.name}</h4>
                          <p>Accompli !</p>
                          <div className="badge-tooltip">{badge.description}</div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="no-badges">Continuez à étudier pour obtenir des badges !</p>
                  )}
                </div>
              </div>
              <div className="dashboard-card wide-card">
                <h3>Prochains Badges</h3>
                <div className="badges-container">
                  {badgeData.upcoming.length > 0 ? (
                    badgeData.upcoming.map((badge, index) => (
                      <div key={index} className="badge-item upcoming">
                        <div className="badge-icon dimmed">{badge.icon}</div>
                        <div className="badge-details">
                          <h4>{badge.name}</h4>
                          <div className="progress-container">
                            <div className="progress-bar" style={{ width: `${calculateProgress(badge.current, badge.threshold)}%` }}></div>
                            <span className="progress-text">
                              {badge.current}/{badge.threshold} ({calculateProgress(badge.current, badge.threshold)}%)
                            </span>
                          </div>
                          <div className="badge-tooltip">{badge.description}</div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="all-badges">Félicitations ! Vous avez obtenu tous les badges disponibles !</p>
                  )}
                </div>
              </div>
            </div>
            <div className="celebration-section">
              <div className="celebrating-card">
                <div className="confetti-animation">
                  {Array.from({ length: 10 }).map((_, i) => <div key={i} className="confetti-piece"></div>)}
                </div>
                <h3>{celebrationTitle}</h3>
                <div className="celebration-stats">
                  <div className="stat-highlight">
                    <span className="stat-number">{celebrationStats.quizCount}</span>
                    <span className="stat-label">Quiz Complétés</span>
                  </div>
                  <div className="stat-highlight">
                    <span className="stat-number">{celebrationStats.flashcardsStudied}</span>
                    <span className="stat-label">Flashcards Étudiées</span>
                  </div>
                  <div className="stat-highlight">
                    <span className="stat-number">{celebrationStats.studyDays}</span>
                    <span className="stat-label">Jours d'Étude</span>
                  </div>
                </div>
                <div className="achievement-message">
                  <p>{celebrationMessage}</p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AccountDashboard;